import type { SymmetryAxis } from "@pdfking/filling"
import type { Distance, Pixel, Point, SpatialRectangle } from "@pdfking/math"
import { Round } from "@pdfking/math"
import { QUARTER_TURN, ZERO_DISTANCE, ZERO_POINT } from "@pdfking/math/constants"
import { CM, MM, QuantityToString } from "@pdfking/quantity"
import { DISTANCE } from "@pdfking/quantity/constants"
import type { ColorID } from "@pdfking/renderer-api"
import type { PageShape } from "@pdfking/renderer-api"

import type { ShapeSequence } from "#internal"

import { MEASURE_COLOR_ID, PAPER_COLOR_ID } from "../../data/ColorList"
import { CreateGroup } from "../../shapes/CreateGroup"
import { CreatePath } from "../../shapes/CreatePath"
import { CreateRect } from "../../shapes/CreateRect"
import { CreateText } from "../../shapes/CreateText"

const MEASURE_ARROW = MM(2)
const DYNAMIC_OFFSET = MM(5)

// Centimetres to one decimal, millimetres under one.
const format_measure = (distance: Distance): string =>
  QuantityToString(
    distance,
    DISTANCE,
    distance >= CM(1) ? { unit: "cm", decimals: 1 } : { unit: "mm", decimals: 0 }
  ).toUpperCase()

/** A measure `length` long, drawn along x from the origin: ticks at both ends,
 * arrows between them and the label centred, or a plain line when the label
 * does not fit. */
const measure = (length: Distance, stroke_color: ColorID, text_color: ColorID): PageShape[] => {
  const tick = (at: Distance): PageShape =>
    CreatePath(
      [
        { x: at, y: -MEASURE_ARROW as Distance },
        { x: at, y: MEASURE_ARROW },
      ],
      stroke_color
    )
  const ticks = [tick(ZERO_DISTANCE), tick(length)]
  const line = [ZERO_POINT, { x: length, y: ZERO_DISTANCE }] as Point<Distance>[]

  const text = format_measure(length)
  const text_width = MM(1.2) * text.length + MM(4)
  if (Round(length / MM(1)) <= Round(text_width / MM(1))) {
    return [...ticks, CreatePath(line, stroke_color)]
  }

  // An arrow head at `at`, pointing back along `dir`.
  const arrow = (at: number, dir: number): Point<Distance>[] =>
    [
      { x: at, y: 0 },
      { x: at + dir, y: -MEASURE_ARROW / 2 },
      { x: at + dir, y: MEASURE_ARROW / 2 },
      { x: at, y: 0 },
    ] as Point<Distance>[]
  const half = MM(2) + text_width / 2
  const label = {
    x: length / 2 - half,
    y: -MEASURE_ARROW,
    width: half * 2,
    height: MEASURE_ARROW * 2,
  } as SpatialRectangle<Distance>

  return [
    ...ticks,
    CreatePath(
      [...arrow(0, MEASURE_ARROW), ...line, ...arrow(length, -MEASURE_ARROW)],
      stroke_color,
      { closed: true }
    ),
    CreateRect(label, { fill_color: stroke_color }),
    CreateText(label, text, { text_color, font_size: 9 as Pixel }),
  ]
}

export function* PlotMeasureFromGuides(opts: {
  canvas: SpatialRectangle<Distance>
  guides_x: Distance[]
  guides_y: Distance[]
  padding?: Distance
  stroke_color?: ColorID
  text_color?: ColorID
}): ShapeSequence {
  const pad = opts.padding ?? DYNAMIC_OFFSET
  const stroke = opts.stroke_color ?? MEASURE_COLOR_ID
  const text = opts.text_color ?? PAPER_COLOR_ID
  const { x, y, width, height } = opts.canvas

  // `across` is where the measure line sits, `pad` away from the canvas (y up:
  // down at the bottom, up at the top). The plain edge measures its whole run
  // in one, the `full` one every span between consecutive guides.
  const edges: Array<{
    axes: SymmetryAxis
    guides: Distance[]
    start: number
    across: number
    full: boolean
  }> = [
    { axes: "x", guides: opts.guides_x, start: x, across: y + height + pad, full: false },
    { axes: "x", guides: opts.guides_x, start: x, across: y - pad, full: true },
    { axes: "y", guides: opts.guides_y, start: y, across: x - pad, full: false },
    { axes: "y", guides: opts.guides_y, start: y, across: x + width + pad, full: true },
  ]

  for (const { axes, guides, start, across, full } of edges) {
    if (guides.length < 2) continue
    const pairs = full
      ? guides.slice(0, -1).map((g, i) => [g, guides[i + 1]!] as const)
      : [[guides[0]!, guides[guides.length - 1]!] as const]
    for (const [g1, g2] of pairs) {
      const at = start + g1
      // A y measure is the x one given a quarter turn.
      yield* CreateGroup(
        measure((g2 - g1) as Distance, stroke, text),
        axes === "x"
          ? { translate: { x: at, y: across } as Point<Distance> }
          : { rotation: QUARTER_TURN, translate: { x: across, y: at } as Point<Distance> }
      )
    }
  }
}
